/**
 * What the admin console needs to know about Animal Party.
 *
 * Everything here is MIRRORED from `@apx/game-animalparty`. The package cannot
 * be imported at runtime: it reaches `node:crypto`, and this is a browser
 * bundle. So the tables are copied, and `animalparty.test.ts` (which runs in
 * Node, where importing the package is free) compares every one of them with
 * the package. A change there that is not made here fails that test.
 *
 * One copy, read by the payout-multipliers editor and by the transaction-row
 * formatters, so the two cannot name a spot or a result differently.
 */

/** English name per result: the nine animals on the ring, and the King in the centre. */
export const ANP_OUTCOME_NAMES: Readonly<Record<string, string>> = {
  panda: 'Panda',
  rabbit: 'Rabbit',
  monkey: 'Monkey',
  tiger: 'Tiger',
  zebra: 'Zebra',
  elephant: 'Elephant',
  gorilla: 'Gorilla',
  dragon: 'Dragon',
  unicorn: 'Unicorn',
  king: 'King',
};

export interface AnimalPartySpot {
  /** Wire bet type. */
  key: string;
  /** English label for an operator. */
  label: string;
  kind: 'animal' | 'group';
  /** The results this spot wins on. No spot wins on the King. */
  winsOn: readonly string[];
}

/** The eleven spots in paytable order: nine single animals, then the two groups. */
export const ANP_SPOTS: readonly AnimalPartySpot[] = [
  { key: 'ANP_PANDA', label: 'Panda', kind: 'animal', winsOn: ['panda'] },
  { key: 'ANP_RABBIT', label: 'Rabbit', kind: 'animal', winsOn: ['rabbit'] },
  { key: 'ANP_MONKEY', label: 'Monkey', kind: 'animal', winsOn: ['monkey'] },
  { key: 'ANP_TIGER', label: 'Tiger', kind: 'animal', winsOn: ['tiger'] },
  { key: 'ANP_ZEBRA', label: 'Zebra', kind: 'animal', winsOn: ['zebra'] },
  { key: 'ANP_ELEPHANT', label: 'Elephant', kind: 'animal', winsOn: ['elephant'] },
  { key: 'ANP_GORILLA', label: 'Gorilla', kind: 'animal', winsOn: ['gorilla'] },
  { key: 'ANP_DRAGON', label: 'Dragon', kind: 'animal', winsOn: ['dragon'] },
  { key: 'ANP_UNICORN', label: 'Unicorn', kind: 'animal', winsOn: ['unicorn'] },
  {
    key: 'ANP_LAND',
    label: 'Land Animals',
    kind: 'group',
    winsOn: ['tiger', 'rabbit', 'monkey', 'zebra'],
  },
  { key: 'ANP_MAGIC', label: 'Magic Animals', kind: 'group', winsOn: ['unicorn', 'dragon'] },
];

/** Built-in total-return bps per spot. Mirrors `DEFAULT_ANP_RETURN_BPS`. */
export const ANP_DEFAULT_RETURNS: Readonly<Record<string, number>> = {
  ANP_PANDA: 50_000,
  ANP_RABBIT: 60_000,
  ANP_MONKEY: 80_000,
  ANP_TIGER: 100_000,
  ANP_ZEBRA: 120_000,
  ANP_ELEPHANT: 160_000,
  ANP_GORILLA: 250_000,
  ANP_DRAGON: 460_000,
  ANP_UNICORN: 880_000,
  ANP_LAND: 21_000,
  ANP_MAGIC: 300_000,
};

/**
 * Integer outcome weights of the table new rounds draw with. Mirrors
 * `DEFAULT_ANP_WEIGHTS.outcomes`. It is a versioned constant in the package and
 * not an admin surface, which is why it can be a constant here too: an editor
 * can show each spot's exact RTP without asking the server.
 */
export const ANP_OUTCOME_WEIGHTS: Readonly<Record<string, number>> = {
  panda: 60_720,
  rabbit: 50_600,
  monkey: 37_950,
  tiger: 30_360,
  zebra: 25_300,
  elephant: 18_975,
  gorilla: 12_144,
  dragon: 6_600,
  unicorn: 3_450,
  king: 70_151,
};

/** The step a multiplier moves in. Mirrors `RETURN_BPS_STEP`. */
export const ANP_RETURN_BPS_STEP = 1_000;

/** Basis-point scale: returnBps / BPS_SCALE = total-return multiplier. */
const BPS_SCALE = 10_000;

const TOTAL_WEIGHT = Object.values(ANP_OUTCOME_WEIGHTS).reduce((s, w) => s + w, 0);
const SPOT_BY_KEY = new Map(ANP_SPOTS.map((s) => [s.key, s]));

/** A spot's chance of winning, as a fraction of 1. 0 for an unknown key. */
export function animalPartyWinChance(betType: string): number {
  const spot = SPOT_BY_KEY.get(betType);
  if (!spot) return 0;
  const wins = spot.winsOn.reduce((s, o) => s + (ANP_OUTCOME_WEIGHTS[o] ?? 0), 0);
  return wins / TOTAL_WEIGHT;
}

/** A spot's RTP at a given total-return bps: `P(win) x multiple`. */
export function animalPartySpotRtp(betType: string, returnBps: number): number {
  return animalPartyWinChance(betType) * (returnBps / BPS_SCALE);
}

/** Operator label for a bet type, or null when it is not one of this table's. */
export function animalPartyBetLabel(betType: string): string | null {
  return SPOT_BY_KEY.get(betType)?.label ?? null;
}

/** The group a result belongs to, for a transaction row. Null for the three
 *  animals in neither group, and for the King. */
export function animalPartyGroupOf(outcome: string): string | null {
  for (const spot of ANP_SPOTS) {
    if (spot.kind === 'group' && spot.winsOn.includes(outcome)) return spot.label;
  }
  return null;
}
